import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Eye, EyeOff, Pencil, Trash2, Download, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API, fmtPrice, loadCatalog } from "../../lib/store";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { InventoryForm, CATEGORY_OPTIONS } from "./InventoryForm";

export function InventoryTab({ auth }) {
  const [data, setData] = useState({ total: 0, products: [], syncEnabled: true });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const token = auth.headers.Authorization;
  const load = useCallback(async (s, c) => {
    try {
      const params = { search: s || "", category: c === "all" ? "" : c, limit: 100 };
      const r = await axios.get(`${API}/admin/inventory/products`, { headers: { Authorization: token }, params });
      setData(r.data);
      setSelected([]);
    } catch (e) {
      console.warn("Inventory load failed:", e?.message);
      toast.error("Could not load products");
    }
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => load(search, category), 350);
    return () => clearTimeout(t);
  }, [search, category, load]);

  const refresh = async () => { await load(search, category); await loadCatalog(); };

  const toggleSync = async (enabled) => {
    const msg = enabled
      ? "Turn the Shopify sync back ON? Prices and products will update from Shopify every 15 minutes again."
      : "Turn the Shopify sync OFF?\n\nYour 1,300+ existing products stay exactly as they are — nothing is deleted. Prices will simply stop updating from Shopify, and you manage everything here.";
    if (!window.confirm(msg)) return;
    try {
      await axios.post(`${API}/admin/sync-toggle`, { enabled }, auth);
      setData((d) => ({ ...d, syncEnabled: enabled }));
      toast.success(enabled ? "Shopify sync is ON" : "Shopify sync is OFF — products are managed here now");
    } catch { toast.error("Could not change the sync setting"); }
  };

  const hideShow = async (handle, hide) => {
    try {
      await axios.patch(`${API}/admin/inventory/products/${handle}`, { hidden: hide }, auth);
      toast.success(hide ? "Hidden from the site" : "Visible on the site again");
      refresh();
    } catch { toast.error("Could not update"); }
  };

  const remove = async (p) => {
    const isManual = p.source === "manual";
    const msg = isManual
      ? `Delete "${p.title}" permanently?`
      : `Remove "${p.title}" from the site?\n\nIt came from Shopify, so it will be hidden (and stay hidden) — you can bring it back anytime with Show.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await axios.delete(`${API}/admin/inventory/products/${p.handle}`, auth);
      toast.success(r.data.deleted ? "Product deleted" : "Product hidden from the site");
      refresh();
    } catch { toast.error("Could not remove"); }
  };

  const bulk = async (action) => {
    const labels = { hide: "Hide", show: "Show", delete: "Remove" };
    if (!window.confirm(`${labels[action]} ${selected.length} selected product${selected.length > 1 ? "s" : ""}?`)) return;
    try {
      await axios.post(`${API}/admin/inventory/bulk`, { handles: selected, action }, auth);
      toast.success(`${selected.length} products updated`);
      refresh();
    } catch { toast.error("Bulk action failed"); }
  };

  const downloadCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/inventory/export`, { headers: { Authorization: token }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "regal-inventory.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Inventory backup downloaded");
    } catch { toast.error("Export failed"); }
  };

  const importCsv = async (fileList) => {
    const f = fileList?.[0];
    if (!f) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await axios.post(`${API}/admin/inventory/import`, fd, auth);
      toast.success(`Import done — ${r.data.created} added, ${r.data.updated} updated`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed — check the CSV format");
    } finally { setImporting(false); }
  };

  const toggleSelect = (h) => setSelected((s) => (s.includes(h) ? s.filter((x) => x !== h) : [...s, h]));

  return (
    <div className="space-y-6" data-testid="inventory-tab-content">
      {/* Action bar */}
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-12 px-7 transition-colors duration-300" data-testid="inventory-add-btn">
          <Plus size={16} className="mr-2" /> Add Product
        </Button>
        <Button variant="outline" onClick={downloadCsv} className="rounded-none border-[#d8cdb5] h-12" data-testid="inventory-export-btn">
          <Download size={15} className="mr-2" /> Backup (CSV)
        </Button>
        <label>
          <Button variant="outline" asChild className="rounded-none border-[#d8cdb5] h-12 cursor-pointer">
            <span><Upload size={15} className="mr-2" /> {importing ? "Importing..." : "Restore / Import CSV"}</span>
          </Button>
          <input type="file" accept=".csv" className="hidden" onChange={(e) => { importCsv(e.target.files); e.target.value = ""; }} data-testid="inventory-import-input" />
        </label>
      </div>

      {/* Sync control */}
      <div className="bg-white border border-[#eee6d6] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="inventory-sync-card">
        <div>
          <p className="text-sm font-medium text-[#171310] flex items-center gap-2">
            <RefreshCw size={15} className="text-[#a8842c]" /> Shopify Sync is {data.syncEnabled ? "ON" : "OFF"}
          </p>
          <p className="text-xs text-[#8f8571] mt-1 max-w-xl">
            {data.syncEnabled
              ? "Products & prices update from your Shopify store every 15 minutes. Anything you add or edit here is protected — the sync never overwrites it."
              : "The sync is off. Your products are safe and managed entirely here. Turn it back on anytime."}
          </p>
        </div>
        <Switch checked={data.syncEnabled} onCheckedChange={toggleSync} data-testid="inventory-sync-toggle" />
      </div>

      {/* Search + filter */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9482]" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products by name or code..." className="rounded-none border-[#d8cdb5] pl-9 h-11" data-testid="inventory-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[190px] rounded-none border-[#d8cdb5] h-11 bg-white" data-testid="inventory-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-[#e8e0d0] rounded-none">
            <SelectItem value="all">All categories</SelectItem>
            {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk bar */}
      {selected.length > 0 && (
        <div className="bg-[#171310] text-[#e9d9ac] px-4 py-3 flex flex-wrap items-center gap-3" data-testid="inventory-bulk-bar">
          <span className="text-sm">{selected.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => bulk("hide")} className="rounded-none border-[#e9d9ac]/40 bg-transparent text-[#e9d9ac] hover:bg-[#e9d9ac] hover:text-[#171310] h-8 text-xs uppercase tracking-wider" data-testid="inventory-bulk-hide">Hide</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("show")} className="rounded-none border-[#e9d9ac]/40 bg-transparent text-[#e9d9ac] hover:bg-[#e9d9ac] hover:text-[#171310] h-8 text-xs uppercase tracking-wider" data-testid="inventory-bulk-show">Show</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("delete")} className="rounded-none border-[#8c2f1b] bg-transparent text-[#ff9b84] hover:bg-[#8c2f1b] hover:text-white h-8 text-xs uppercase tracking-wider" data-testid="inventory-bulk-delete">Remove</Button>
        </div>
      )}

      {/* Product list */}
      <p className="text-xs text-[#8f8571]">{data.total} products{data.total > data.products.length ? ` — showing first ${data.products.length}, use search to find others` : ""}</p>
      <div className="bg-white border border-[#eee6d6] divide-y divide-[#f2ecdd]" data-testid="inventory-list">
        {data.products.map((p) => (
          <div key={p.handle} className={`flex items-center gap-3 px-4 py-3 ${p.hidden ? "opacity-60" : ""}`} data-testid="inventory-row">
            <Checkbox checked={selected.includes(p.handle)} onCheckedChange={() => toggleSelect(p.handle)} data-testid="inventory-row-check" />
            <img src={p.images?.[0]} alt="" loading="lazy" className="w-12 h-12 object-contain bg-[#faf7f0] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#171310] truncate">{p.title}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                <span className="text-xs font-medium text-[#5c5240]">{fmtPrice(p.price)}</span>
                {p.source === "manual" && <Badge variant="outline" className="rounded-none border-[#a8842c] text-[#a8842c] text-[9px] px-1.5 py-0">ADDED BY YOU</Badge>}
                {p.hidden && <Badge className="rounded-none bg-[#5c5240] text-white text-[9px] px-1.5 py-0 hover:bg-[#5c5240]">HIDDEN</Badge>}
                {p.available === false && <Badge className="rounded-none bg-[#8c2f1b] text-white text-[9px] px-1.5 py-0 hover:bg-[#8c2f1b]">SOLD OUT{p.soldOutAt ? ` — OFF SITE ${new Date(new Date(p.soldOutAt).getTime() + 14 * 86400000).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""}</Badge>}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button variant="outline" size="sm" onClick={() => { setEditing(p); setFormOpen(true); }} className="rounded-none border-[#d8cdb5] h-9 text-xs uppercase tracking-wider" data-testid="inventory-edit-btn">
                <Pencil size={13} className="mr-1.5" /> Edit
              </Button>
              <Button variant="outline" size="icon" onClick={() => hideShow(p.handle, !p.hidden)} title={p.hidden ? "Show on site" : "Hide from site"} className="rounded-none border-[#d8cdb5] h-9 w-9" data-testid="inventory-hide-btn">
                {p.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
              </Button>
              <Button variant="outline" size="icon" onClick={() => remove(p)} className="rounded-none border-[#d8cdb5] h-9 w-9 text-[#8c2f1b] hover:bg-[#8c2f1b] hover:text-white" data-testid="inventory-delete-btn">
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
        {data.products.length === 0 && <p className="text-sm text-[#8f8571] text-center py-10">No products match your search.</p>}
      </div>

      <InventoryForm open={formOpen} onOpenChange={setFormOpen} product={editing} auth={auth} onSaved={refresh} />
    </div>
  );
}
